import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import type {
  LiveViewQueue,
  QueueRagStatus,
  QueueType,
} from "@/features/deployment-dashboard/live-view/model/queue";
import {
  formatQueueWaitCard,
  isNearEmptyQueueWait,
} from "@/features/deployment-dashboard/shared/queue/queue-wait";

export type { LiveViewQueue, QueueRagStatus, QueueType };

export interface LiveViewQueuesTabProps {
  queues: LiveViewQueue[];
  selectedQueueId: string | null;
  onToggleQueue: (queueId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
}

const statusLabels: Record<QueueRagStatus, string> = {
  green: "Short wait",
  amber: "Moderate wait",
  red: "Long wait",
};

const statusPalette: Record<QueueRagStatus, "success" | "warning" | "error"> = {
  green: "success",
  amber: "warning",
  red: "error",
};

const queueTypeLabels: Record<QueueType, string> = {
  general: "General",
  priority: "Priority",
};

export function LiveViewQueuesTab({
  queues,
  selectedQueueId,
  onToggleQueue,
  isLoading = false,
  error = null,
}: LiveViewQueuesTabProps) {
  return (
    <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", p: 2 }}>
      {isLoading ? (
        <Box sx={{ p: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Loading queues…
          </Typography>
        </Box>
      ) : error !== null ? (
        <Box sx={{ p: 1.5 }}>
          <Typography variant="body2" color="error">
            Unable to load queues.
          </Typography>
        </Box>
      ) : queues.length === 0 ? (
        <Box
          sx={(theme) => ({
            p: 1.5,
            borderRadius: 1,
            color: "text.secondary",
            backgroundColor: alpha(theme.palette.text.primary, 0.04),
          })}
        >
          <Typography variant="body2">No queues found.</Typography>
        </Box>
      ) : (
        <List disablePadding aria-label="Queues">
          <Stack spacing={1}>
            {queues.map((queue) => {
              const paletteColor = queue.status === null ? "default" : statusPalette[queue.status];
              const statusColor = queue.status === null ? "text.disabled" : `${paletteColor}.main`;
              const isStale =
                queue.peopleDataStatus === "stale" || queue.waitDataStatus === "stale";
              const isNearEmpty = isNearEmptyQueueWait({
                value: queue.estimatedWaitMinutes,
                dataStatus: queue.waitDataStatus ?? "unavailable",
                qualityReasons: queue.waitQualityReasons,
              });

              return (
                <ListItem key={queue.queue_id} disablePadding>
                  <Paper
                    component="button"
                    type="button"
                    variant="outlined"
                    onClick={() => onToggleQueue(queue.queue_id)}
                    aria-pressed={selectedQueueId === queue.queue_id}
                    sx={(theme) => ({
                      width: "100%",
                      p: 1.25,
                      borderLeft: 4,
                      borderLeftColor: statusColor,
                      textAlign: "left",
                      cursor: "pointer",
                      opacity: isStale ? 0.65 : 1,
                      backgroundColor:
                        selectedQueueId === queue.queue_id
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "background.paper",
                      "&:hover": { backgroundColor: "action.hover" },
                    })}
                  >
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                          {queue.queue_display_name}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75 }}>
                          <Chip
                            label={queueTypeLabels[queue.queue_type]}
                            variant="outlined"
                            size="small"
                            sx={{ height: 22 }}
                          />
                          <Chip
                            label={
                              isStale
                                ? "Stale"
                                : queue.status === null
                                  ? "Unavailable"
                                  : statusLabels[queue.status]
                            }
                            color={paletteColor}
                            size="small"
                            sx={{ height: 22, fontWeight: 700 }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                          <GroupsOutlinedIcon fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontSize: "0.8125rem", fontWeight: 700 }}
                          >
                            {queue.peopleInQueue == null
                              ? "Unavailable"
                              : `${queue.peopleInQueue} ${queue.peopleInQueue === 1 ? "person" : "people"}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                          <AccessTimeOutlinedIcon fontSize="small" color="action" />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontSize: "0.8125rem", fontWeight: 700 }}
                          >
                            {formatQueueWaitCard(queue.estimatedWaitMinutes, isNearEmpty)}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                </ListItem>
              );
            })}
          </Stack>
        </List>
      )}
    </Box>
  );
}
